import RBush from 'rbush';
import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { commarize } from 'shared/common/utils/number';
import {
  BALLOON_HOVERED_Z_INDEX,
  BALLOON_PROMO_Z_INDEX,
  DOT_SIZE_SMALL_REDESIGN,
  BALLOON_ACTIVE_Z_INDEX,
} from 'shared/map-search/constants/map';
import { EClustersType } from 'shared/map-search/types/clustersType';
import {
  IFeatureProperties,
  IRBushItem,
  type TFeature,
  type TFeaturePropertiesRequiredPrice,
} from 'shared/map-search/types/map';

import { BalloonPromoContent } from '../BalloonPromoContent';

const REDESIGN_BALLOON_PIN_GAP = 4;
const REDESIGN_BALLOON_SHIFT = DOT_SIZE_SMALL_REDESIGN + REDESIGN_BALLOON_PIN_GAP;

interface IBalloonPromoManagerOptions {
  map: YMaps.Map;
  rbush: RBush<IRBushItem>;
  pointType: EClustersType | null;
  isRedesign: boolean;
}

/**
 * @todo разработка в MNZ-700
 * Менеджер для промо-слоя балунов
 * Используется для отображения пинов с пульсацией
 */
export function defineBalloonPromoManager(ymaps: YMaps.IYMaps) {
  if (ymaps.modules.isDefined('BalloonPromoManager')) {
    return;
  }

  ymaps.modules.define<
    [YMaps.ITemplateLayoutFactory, typeof YMaps.shape.Rectangle, typeof YMaps.geometry.pixel.Rectangle]
  >(
    'BalloonPromoManager',
    ['templateLayoutFactory', 'shape.Rectangle', 'geometry.pixel.Rectangle'],
    (
      provide,
      templateLayoutFactory: YMaps.ITemplateLayoutFactory,
      shapeRectangle: typeof YMaps.shape.Rectangle,
      geometryPixelRectangle: typeof YMaps.geometry.pixel.Rectangle,
    ) => {
      class BalloonPromoManager {
        public readonly objectManager: YMaps.ObjectManager;
        public pointType: EClustersType | null;

        private map: YMaps.Map;
        private rbush: RBush<IRBushItem>;
        private isDetailsVisible = false;
        private isRedesign;

        public constructor(options: IBalloonPromoManagerOptions) {
          this.map = options.map;
          this.rbush = options.rbush;
          this.objectManager = new ymaps.ObjectManager();
          this.pointType = options.pointType;
          this.isRedesign = options.isRedesign;
        }

        public updateBalloons = (isPromoDetailsVisible?: boolean) => {
          this.isDetailsVisible = !!isPromoDetailsVisible || this.isRedesign;

          const bounds = this.map.getBounds();
          const rBushItems = this.rbush.search({
            minX: bounds[0][0],
            minY: bounds[0][1],
            maxX: bounds[1][0],
            maxY: bounds[1][1],
          });

          this.objectManager.removeAll();

          const features = rBushItems
            .map(this.prepareBalloon)
            .filter(feature => {
              if (!feature) {
                return false;
              }

              if (feature.properties.type === 'peas') {
                return false;
              }

              const isPulsed = !!feature.properties.isPulsedGlow;

              if (this.pointType) {
                return (
                  isPulsed && (this.pointType === EClustersType.Detailed || !!feature.properties.favoriteIds?.length)
                );
              }

              return isPulsed;
            })
            .sort((a, b) => {
              if (a?.properties.label && !b?.properties.label) {
                return -1;
              }
              if (!a?.properties.label && b?.properties.label) {
                return 1;
              }

              return 0;
            }) as TFeature[];

          this.objectManager.add({
            type: 'FeatureCollection',
            features,
          });
        };

        public updateBalloonProperties = (
          featureId: string,
          properties: TFeaturePropertiesRequiredPrice,
          isPromoDetailsVisible?: boolean,
        ) => {
          this.isDetailsVisible = !!isPromoDetailsVisible || this.isRedesign;

          this.objectManager.objects.setObjectOptions(featureId, {
            iconLayout: this.prepareBalloonLayout(properties as TFeaturePropertiesRequiredPrice),
            zIndex: this.getBalloonZIndex(properties),
          });
        };

        private prepareBalloon = ({ feature }: IRBushItem) => {
          const { properties } = feature;

          if (!properties.minPrice && !properties.specialPromo) {
            return null;
          }

          return {
            ...feature,
            options: {
              iconLayout: this.prepareBalloonLayout(properties as TFeaturePropertiesRequiredPrice),
              zIndex: this.getBalloonZIndex(properties),
            },
          };
        };

        private getBalloonZIndex = (properties: IFeatureProperties) => {
          if (properties.isActive) {
            return BALLOON_ACTIVE_Z_INDEX;
          }

          return properties.isHovered ? BALLOON_HOVERED_Z_INDEX : BALLOON_PROMO_Z_INDEX;
        };

        private prepareBalloonLayout = (properties: TFeaturePropertiesRequiredPrice) => {
          const content = this.prepareBalloonContent(properties);
          const pointType = this.pointType;
          const isRedesign = this.isRedesign;

          return templateLayoutFactory.createClass(content, {
            getShape() {
              // oxlint-disable-next-line typescript/no-explicit-any
              const element: HTMLElement = (this as any).getElement();
              if (!element) {
                return null;
              }

              const wrapperElement = element.firstElementChild;
              if (!wrapperElement) {
                return null;
              }

              const boundingRect = wrapperElement.getBoundingClientRect();
              const offsetX = -boundingRect.width / 2;

              let shiftY = isRedesign ? REDESIGN_BALLOON_SHIFT : 6;

              if (!isRedesign && !pointType) {
                shiftY = 4;
              } else if (!isRedesign) {
                shiftY = pointType === EClustersType.OpenablePeas && properties.count > 1 ? 12 : 6;
              }

              const offsetY = -boundingRect.height - shiftY;

              return new shapeRectangle(
                new geometryPixelRectangle([
                  [offsetX, offsetY],
                  [offsetX + boundingRect.width, offsetY + boundingRect.height],
                ]),
              );
            },
          });
        };

        private prepareBalloonContent(feature: TFeaturePropertiesRequiredPrice) {
          const {
            count,
            minPrice,
            favoriteIds,
            hasNewobject,
            isAnyFromKp,
            isViewed,
            hasDiscountOffers,
            isActive,
            newbuilding,
            clusterOfferIds,
            details,
            detailsV2,
            isHovered,
          } = feature;

          const isClustersRedesign = Boolean(this.pointType);
          const isShowDetails = this.isDetailsVisible && !!details;
          const price = minPrice ? `${count > 1 ? 'от ' : ''}${commarize(minPrice)}` : '';
          const isFavorite = clusterOfferIds ? clusterOfferIds.some(id => favoriteIds?.includes(id)) : false;
          const label = this.isRedesign ? feature?.label?.toLowerCase() : feature?.label;
          const newbuildingId = newbuilding?.id ?? 0;
          const isNewbuildingOnProPlus = !!(newbuilding?.isAnyFicheringPlus || isAnyFromKp);

          return renderToStaticMarkup(
            createElement(BalloonPromoContent, {
              count,
              price,
              isClustersRedesign,
              isShowDetails,
              isFavorite,
              hasNewobject,
              isAnyFromKp,
              isViewed,
              hasDiscountOffers,
              isActive,
              isHovered,
              label,
              details: this.isRedesign ? detailsV2 || details : details,
              pointType: this.pointType,
              newbuildingId,
              isNewbuildingOnProPlus,
              isRedesign: this.isRedesign,
            }),
          );
        }
      }

      provide(BalloonPromoManager);
    },
  );
}
